import { useEffect, useRef, useState } from 'react';
import { fontSources } from '@/lib/curatedFonts';

const NS = 'http://www.w3.org/2000/svg', data = new Map();
const toDataUrl = url => {
  if (!data.has(url)) data.set(url, fetch(url).then(r => (r.ok ? r.blob() : null)).then(b => b && new Promise(res => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(b); })).catch(() => null));
  return data.get(url);
};

const fontRules = () => {
  const rules = [];
  for (const sheet of document.styleSheets) {
    let list; try { list = sheet.cssRules; } catch { continue; }
    for (const r of list) {
      if (r.type !== 5) continue;
      const m = r.style.getPropertyValue('src').match(/url\(["']?([^"')]+)["']?\)/);
      if (m) rules.push({ fam: r.style.getPropertyValue('font-family').replace(/["']/g, '').trim(), url: new URL(m[1], sheet.href || window.location.href).href, weight: r.style.getPropertyValue('font-weight'), style: r.style.getPropertyValue('font-style'), range: r.style.getPropertyValue('unicode-range') });
    }
  }
  fontSources.forEach((url, fam) => rules.push({ fam, url }));
  return rules;
};

// Clone of the engraving area as a standalone SVG (images and fonts inlined later by rasterise).
export const cloneArt = (svg, area, px = 1024) => {
  const art = svg?.querySelector('[data-testid=engraving-area]');
  if (!art) return null;
  const out = document.createElementNS(NS, 'svg'), w = px, h = Math.max(1, Math.round(px * area.h / area.w));
  out.setAttribute('xmlns', NS); out.setAttribute('viewBox', `${area.x} ${area.y} ${area.w} ${area.h}`); out.setAttribute('width', w); out.setAttribute('height', h);
  svg.querySelectorAll('defs').forEach(d => out.append(d.cloneNode(true)));
  out.querySelectorAll('[data-testid=glass-filter]').forEach(f => f.remove());
  out.append(art.cloneNode(true));
  out.querySelectorAll('[data-testid^=object-hitarea]').forEach(n => n.remove());
  return { out, w, h };
};

export const rasterise = async ({ out, w, h }, background = null) => {
  await Promise.all([...out.querySelectorAll('image')].map(async img => {
    const href = img.getAttribute('href') || img.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
    if (href && !href.startsWith('data:')) img.setAttribute('href', (await toDataUrl(new URL(href, window.location.href).href)) || '');
  }));
  const markup = new XMLSerializer().serializeToString(out), used = fontRules().filter(r => r.fam && markup.includes(r.fam));
  const faces = (await Promise.all(used.map(async r => { const src = await toDataUrl(r.url); return src ? `@font-face{font-family:"${r.fam}";src:url(${src});${r.weight ? `font-weight:${r.weight};` : ''}${r.style ? `font-style:${r.style};` : ''}${r.range ? `unicode-range:${r.range};` : ''}}` : ''; }))).join('');
  const final = faces ? markup.replace(/^<svg([^>]*)>/, `<svg$1><style>${faces}</style>`) : markup;
  const url = URL.createObjectURL(new Blob([final], { type: 'image/svg+xml' }));
  try {
    const img = new Image(); img.src = url; await img.decode();
    const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (background) { ctx.fillStyle = background; ctx.fillRect(0, 0, w, h); }
    ctx.drawImage(img, 0, 0, w, h);
    return canvas;
  } catch { return null; } finally { URL.revokeObjectURL(url); }
};

// Rasterises the editor's engraving layer (area only) into a white-on-transparent canvas for the 3D material.
export const captureArt = async (svg, area, px = 1024) => {
  const clone = cloneArt(svg, area, px);
  const canvas = clone && await rasterise(clone);
  if (!canvas) return null;
  const ctx = canvas.getContext('2d'); ctx.globalCompositeOperation = 'source-in'; ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
  return canvas;
};

// Re-captures whenever the editor canvas changes (debounced); returns { canvas, version }.
export const editorSvg = () => { const el = document.querySelector('[data-testid=canvas-art] [data-testid=engraving-canvas]'); return el?.tagName.toLowerCase() === 'svg' ? el : el?.closest('svg') || document.querySelector('[data-testid=canvas-art] svg'); };

export const useArtCapture = (area, enabled = true, capture = captureArt, deps = '') => {
  const [state, setState] = useState({ canvas: null, version: 0 }), timer = useRef(null), busy = useRef(false);
  useEffect(() => {
    if (!enabled) return undefined;
    const host = document.querySelector('[data-testid=canvas-art]');
    const run = async () => {
      if (busy.current) { timer.current = setTimeout(run, 200); return; }
      busy.current = true;
      const canvas = await capture(editorSvg(), area);
      busy.current = false;
      if (canvas) setState(s => ({ canvas, version: s.version + 1 }));
    };
    const schedule = () => { clearTimeout(timer.current); timer.current = setTimeout(run, 220); };
    run();
    const mo = host && new MutationObserver(schedule);
    mo?.observe(host, { subtree: true, childList: true, attributes: true, characterData: true });
    document.fonts?.addEventListener?.('loadingdone', schedule);
    return () => { mo?.disconnect(); clearTimeout(timer.current); document.fonts?.removeEventListener?.('loadingdone', schedule); };
  }, [enabled, area.x, area.y, area.w, area.h, deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
};
